import { ChangeEvent, Fragment, PureComponent, ReactNode } from 'react';
import { ThunkDispatch } from 'redux-thunk';
import { connect } from 'react-redux';
import { Actions } from '../../../../../store/actions';
import {
    copyDurationToAllMed,
    copyRepeatOnInLabInvestigations,
    copyTestOnInLabInvestigations,
    deleteLabTest,
    deleteMedication,
    fetchDrugsSubstitute,
    swapPxElement,
    updateLabToReferId,
    updateTranslate,
    upsertLabTest,
    upsertMedication,
} from '../../../../../store/actions/prescriptionPad';
import {
    getLabsAvailableToRefer,
    getSelectedLabToReferId,
    getTranslate,
    getLabTests,
    getMedications,
    getInventory,
} from '../../../../../store/selectors/prescriptionPad';
import { GlobalState } from '../../../../../store/reducers';
import { Translate } from '../../../../../api/pushVisit';
import { getAllLang } from '../../previewScreen/PreviewPanel';
import { CHANNEL_SUGGESTION, DRUG, LAB_TEST, SELECT, track } from '../../../../../tracking';
import { SpecialityEntity } from '../../../../../api/fetchSpecialityForDoctor';
import { LAB_TESTS_KIND, MEDICINES_KIND } from '../../../trackSource';
import {
    LabTestsEntityOrProceduresEntity,
    MedicationsEntity,
    Prescription,
    Tool,
} from '../../../../../store/types/prescription';
import { getGenieSetting, getPincode } from '../../../../../store/selectors/session';
import MedTableRenderer, { handleNextFocus } from './MedTableRenderer';
import { GeniePadElementsSettingItem } from '../../../../../api/pushGenie';
import PrescribeAutoSuggestV2, { Inventory } from '../PrescribeAutoSuggestV2';
import { medWithQuantity, sanitizeMed } from '../../../../../store/utils';
import { getFlatArray } from './getFlatArray';
import { EkaFocosableElement } from '../../../../common/types/EkaFocusable';
import AddCustomLabTestModal from './AddCustomLabTestModal';
import DrugInteractionExceptionPanel from '../../../../common/DrugInteractionExceptionPanel';
import { PAD_V2_CONTAINER } from '../PadContainer';
import { AnimatePresence } from 'framer-motion';
import HowLabReferWorksDialog from './HowLabReferWorksDialog';
import PackageIcons from './PackageIcons';
import TemplateIcons from './TemplateIcons';
import ConfigureMxTable from './ConfigureMxTable';
import { InvestigationsIcon, MedicinesIcon } from './RowIcons';
import { INVESTIGATIONS } from '../../../../../strings';
import { AntTooltip } from '../../../../common/AntTooltip';
import { isOnboardedForLab, isOnboardedForPharma } from '../../../../../pages/utils';
import { HIV_CONSENT_LAB_TESTS } from '../../../../../constants';
import ConsentFormSuggestion from '../../../../common/ConsentFormSuggestion';
import { setPincode } from '../../../../../store/actions/session';

type Props = {
    kind: typeof MEDICINES_KIND | typeof LAB_TESTS_KIND;
    prescription?: Prescription;
    pincode?: string;
};

type MedTableV2Props = Props & {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    translate: Translate;
    labsAvailableToRefer?: SpecialityEntity[];
    selectedLabToReferId?: string;
    elements: MedicationsEntity[];
    inventory: Inventory;
    elementsConfig: GeniePadElementsSettingItem[];
    showSubstitutes?: boolean;
    sectionName?: string;
};

type State = {
    labReferWorkDialogVisible?: boolean;
    isCheckInventoryLoading: boolean;
    isAddCustomLabTestModalVisible?: boolean;
};

class MedTableV2 extends PureComponent<MedTableV2Props, State> {
    ALL_LANG: { code: string; label: string }[] = getAllLang();
    ELEMENT = this.props.kind === MEDICINES_KIND ? DRUG : LAB_TEST;
    REF_MAP: EkaFocosableElement[][] = [];

    state: State = {
        isCheckInventoryLoading: false,
    };

    showReferLabDialog = () => {
        this.setState({
            labReferWorkDialogVisible: true,
        });
    };
    hideReferLabDialog = () => {
        this.setState({
            labReferWorkDialogVisible: false,
        });
    };

    onTranslateChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        track('PX_TRANSLATE', {
            lang: e.target.value,
            flag: e.target.value,
            action: 'select',
        });
        const { translate } = this.props;
        this.props.dispatch(
            updateTranslate({
                ...translate,
                lang: e.target.value !== 'off' ? e.target.value : translate.lang,
                flag: e.target.value !== 'off' ? true : translate.flag,
                medications: e.target.value === 'off' ? false : true,
            }),
        );
    };

    onSelectLabToRefer = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        track('PX_LAB_TO_REFER', {
            action: e.target.value === 'none' ? 'deselect' : 'select',
        });
        this.props.dispatch(updateLabToReferId(e.target.value));
    };

    upsertMedication = (element: MedicationsEntity, index?: number) => {
        return upsertMedication(medWithQuantity(sanitizeMed(element)), index);
    };

    upsertElement = (element: MedicationsEntity, index?: number, calculateQuantity?: boolean) => {
        const { dispatch, kind } = this.props;

        if (calculateQuantity) {
            dispatch(upsertMedication(sanitizeMed(element), undefined));
        } else {
            dispatch(
                (kind === MEDICINES_KIND ? this.upsertMedication : upsertLabTest)(element, index),
            );
        }
    };

    deleteElement = (id: string) => {
        const { dispatch } = this.props;

        dispatch((this.props.kind === MEDICINES_KIND ? deleteMedication : deleteLabTest)(id));
    };

    onCopyDuration = (medIndex: number) => {
        const medPxElement = this.props.elements[medIndex];

        if (!medPxElement) {
            return;
        }

        this.props.dispatch(copyDurationToAllMed(medPxElement.duration));
    };

    onCopyTestOn = (testOnId: string) => {
        const labTestPxElement = this.props.elements.find(
            (e) => e.id === testOnId,
        ) as LabTestsEntityOrProceduresEntity;

        if (!labTestPxElement?.test_on) {
            return;
        }

        this.props.dispatch(copyTestOnInLabInvestigations(labTestPxElement.test_on));
    };

    onCopyRepeatOn = (repeatOnId: string) => {
        const labTestPxElement = this.props.elements.find(
            (e) => e.id === repeatOnId,
        ) as LabTestsEntityOrProceduresEntity;

        if (!labTestPxElement?.repeat_on) {
            return;
        }

        this.props.dispatch(copyRepeatOnInLabInvestigations(labTestPxElement.repeat_on));
    };

    onSortEnd = (what: keyof Tool, from: number, to: number) => {
        const { dispatch } = this.props;
        dispatch(swapPxElement(what, from, to));
    };

    onElementSelected = (element: MedicationsEntity): void => {
        this.upsertElement(element);
    };

    onElementSuggetionSelected = (element: MedicationsEntity): void => {
        track(SELECT, { element: this.ELEMENT, channel: CHANNEL_SUGGESTION });
        this.onElementSelected(element);
    };

    handleNextFocus = (element: MedicationsEntity) => {
        const { kind, elementsConfig } = this.props;
        const elements = [...(this.props.elements || []), element];

        const flatArrayLength = getFlatArray(elements, kind, false).length;
        setTimeout(() => {
            handleNextFocus(
                elements.length - 1,
                -1,
                flatArrayLength - 1,
                0,
                elements,
                kind,
                elementsConfig,
                this.REF_MAP,
            );
        }, 500);
    };

    addCustomLabTestModalToggleHandler = (r?: MedicationsEntity): void => {
        const { isAddCustomLabTestModalVisible } = this.state;
        this.setState({ isAddCustomLabTestModalVisible: !isAddCustomLabTestModalVisible });
        track('ADD_CUSTOM_LAB_TEST_MODAL', {
            action: isAddCustomLabTestModalVisible ? 'close' : 'open',
            labTestId: r?.id,
            labTestName: r?.name,
        });
    };

    pincodeChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
        const { dispatch } = this.props;
        const value = e.target.value.trim();
        if (value.length > 6) return;
        dispatch(setPincode(e.target.value.trim()));
    };

    checkSubstitutesHandler = () => {
        const { dispatch } = this.props;
        dispatch(fetchDrugsSubstitute());
    };

    render(): ReactNode {
        const {
            kind,
            translate,
            labsAvailableToRefer,
            selectedLabToReferId,
            elements,
            inventory,
            elementsConfig,
            pincode,
            sectionName,
        } = this.props;
        const { labReferWorkDialogVisible, isAddCustomLabTestModalVisible } = this.state;

        const selectedLab =
            selectedLabToReferId &&
            labsAvailableToRefer &&
            labsAvailableToRefer.length > 0 &&
            labsAvailableToRefer[0].network &&
            labsAvailableToRefer[0].network?.length > 0
                ? labsAvailableToRefer[0].network.find(
                      (t) => t._id.toString() === selectedLabToReferId.toString(),
                  )
                : undefined;

        return (
            <Fragment>
                <AnimatePresence>
                    {labReferWorkDialogVisible && (
                        <HowLabReferWorksDialog key="lab-refer" closeMe={this.hideReferLabDialog} />
                    )}
                </AnimatePresence>

                <div className="rounded-8 pt-8 px-16 pb-16 bg-white row focusable">
                    <div className="flex items-center justify-between space-x-8">
                        {kind === MEDICINES_KIND ? (
                            <Fragment>
                                <div className="space-x-10 flex items-center">
                                    <MedicinesIcon />
                                    <p className="font-700 text-16 text-neutral-1000">
                                        {sectionName || 'Medications'}
                                    </p>
                                </div>
                                <div className="flex flex-wrap items-center space-x-12">
                                    {isOnboardedForPharma() && (
                                        <div className="flex items-center space-x-8">
                                            <div>
                                                <svg
                                                    className="w-14 h-15 fill-darwin-neutral-700"
                                                    viewBox="0 0 14 15"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M7 14.75C3.11719 14.75 0 11.6328 0 7.75C0 3.89453 3.11719 0.75 7 0.75C10.8555 0.75 14 3.89453 14 7.75C14 11.6328 10.8555 14.75 7 14.75ZM5.90625 9.9375C5.52344 9.9375 5.25 10.2383 5.25 10.5938C5.25 10.9766 5.52344 11.25 5.90625 11.25H8.09375C8.44922 11.25 8.75 10.9766 8.75 10.5938C8.75 10.2383 8.44922 9.9375 8.09375 9.9375H7.875V7.53125C7.875 7.17578 7.57422 6.875 7.21875 6.875H5.90625C5.52344 6.875 5.25 7.17578 5.25 7.53125C5.25 7.91406 5.52344 8.1875 5.90625 8.1875H6.5625V9.9375H5.90625ZM7 6C7.46484 6 7.875 5.61719 7.875 5.125C7.875 4.66016 7.46484 4.25 7 4.25C6.50781 4.25 6.125 4.66016 6.125 5.125C6.125 5.61719 6.50781 6 7 6Z" />
                                                </svg>
                                            </div>
                                            <p className="text-13">Delivery Pincode:</p>
                                            <input
                                                value={pincode}
                                                placeholder="pincode"
                                                className="border rounded-4 w-72 px-8"
                                                onChange={this.pincodeChangeHandler}
                                                type="number"
                                            />
                                            <button
                                                className="flex items-center space-x-8 px-12 border border-darwin-primary-500 rounded-4"
                                                onClick={this.checkSubstitutesHandler}
                                            >
                                                <span>
                                                    <svg
                                                        className="w-12 h-13 fill-darwin-primary-500"
                                                        viewBox="0 0 12 13"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M10.6406 7.27344C10.9453 7.36719 11.1094 7.67188 11.0156 7.97656C10.3594 10.2031 8.29688 11.75 5.97656 11.75C4.125 11.75 2.41406 10.7422 1.47656 9.17188V11.5625C1.47656 11.8906 1.24219 12.125 0.914062 12.125C0.609375 12.125 0.351562 11.8906 0.351562 11.5625V7.8125C0.351562 7.50781 0.609375 7.25 0.914062 7.25H4.66406C4.99219 7.25 5.22656 7.50781 5.22656 7.8125C5.22656 8.14062 4.96875 8.375 4.66406 8.375H2.29688C3 9.73438 4.40625 10.625 5.97656 10.625C7.80469 10.625 9.44531 9.40625 9.9375 7.64844C10.0312 7.34375 10.3359 7.17969 10.6406 7.27344ZM11.0625 0.875C11.3672 0.875 11.625 1.13281 11.625 1.4375V5.1875C11.625 5.51562 11.3672 5.75 11.0625 5.75H7.3125C6.98438 5.75 6.75 5.51562 6.75 5.1875C6.75 4.88281 6.98438 4.625 7.3125 4.625H9.65625C8.95312 3.28906 7.54688 2.375 6 2.375C4.17188 2.375 2.53125 3.61719 2.01562 5.35156C1.94531 5.65625 1.64062 5.82031 1.33594 5.72656C1.03125 5.65625 0.867188 5.32812 0.9375 5.04688C1.59375 2.82031 3.67969 1.25 6 1.25C7.85156 1.25 9.5625 2.28125 10.5 3.85156V1.4375C10.5 1.13281 10.7344 0.875 11.0625 0.875Z" />
                                                    </svg>
                                                </span>
                                                <span className="text-darwin-primary-500">
                                                    Check
                                                </span>
                                            </button>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-16 flex-wrap">
                                        <div className="flex items-center space-x-4 text-neutral-black text-12 leading-3 bg-neutral-9 rounded-4 px-8 py-2">
                                            <div className="flex items-center justify-center w-12 h-12">
                                                <svg
                                                    className="w-12 h-12 fill-neutral-3"
                                                    role="img"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                >
                                                    <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 464c-114.7 0-208-93.31-208-208S141.3 48 256 48s208 93.31 208 208S370.7 464 256 464zM296 336h-16V248C280 234.8 269.3 224 256 224H224C210.8 224 200 234.8 200 248S210.8 272 224 272h8v64h-16C202.8 336 192 346.8 192 360S202.8 384 216 384h80c13.25 0 24-10.75 24-24S309.3 336 296 336zM256 192c17.67 0 32-14.33 32-32c0-17.67-14.33-32-32-32S224 142.3 224 160C224 177.7 238.3 192 256 192z"></path>
                                                </svg>
                                            </div>
                                            <div className="flex items-baseline space-x-4">
                                                <p>Default Instructions:</p>
                                                <select
                                                    tabIndex={-1}
                                                    value={
                                                        translate.flag &&
                                                        (translate.medications ||
                                                            translate.medications === undefined)
                                                            ? translate.lang
                                                            : 'off'
                                                    }
                                                    onChange={this.onTranslateChange}
                                                    className="bg-transparent text-12 cursor-pointer"
                                                >
                                                    <option value={'off'}>{'Off'}</option>
                                                    {this.ALL_LANG.map((l) => (
                                                        <option key={l.code} value={l.code}>
                                                            {l.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <TemplateIcons
                                            pxToolKey={'medications'}
                                            withPeak={true}
                                            isModal={false}
                                        />
                                        <ConfigureMxTable />
                                    </div>
                                </div>
                            </Fragment>
                        ) : (
                            <Fragment>
                                <div className="space-x-10 flex items-center">
                                    <InvestigationsIcon />
                                    <p className="font-700 text-16 text-neutral-1000">
                                        {sectionName || INVESTIGATIONS}
                                    </p>
                                </div>

                                <div className="flex items-center space-x-16 mr-16">
                                    <AntTooltip
                                        placement="left"
                                        content="Add a lab test"
                                        getPopupContainer={() =>
                                            document.getElementById('add-lab-test') || document.body
                                        }
                                    >
                                        <div
                                            onClick={() =>
                                                this.addCustomLabTestModalToggleHandler()
                                            }
                                            id="add-lab-test"
                                        >
                                            <svg
                                                className="w-22 h-22 fill-neutral-3 cursor-pointer"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                            >
                                                <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM232 344V280H168c-13.3 0-24-10.7-24-24s10.7-24 24-24h64V168c0-13.3 10.7-24 24-24s24 10.7 24 24v64h64c13.3 0 24 10.7 24 24s-10.7 24-24 24H280v64c0 13.3-10.7 24-24 24s-24-10.7-24-24z" />
                                            </svg>
                                        </div>
                                    </AntTooltip>
                                    {isOnboardedForLab() && (
                                        <PackageIcons
                                            pxToolKey={'labTests'}
                                            withPeak={true}
                                            isModal={false}
                                        />
                                    )}

                                    <TemplateIcons
                                        pxToolKey={'labTests'}
                                        withPeak={true}
                                        isModal={false}
                                        isForPackages={isOnboardedForLab()}
                                    />
                                    {labsAvailableToRefer &&
                                        labsAvailableToRefer.length > 0 &&
                                        labsAvailableToRefer[0].network &&
                                        labsAvailableToRefer[0].network?.length > 0 && (
                                            <div className="flex items-center space-x-16">
                                                <div className="flex items-center space-x-10 mt-8 ml-16">
                                                    <p
                                                        onClick={this.showReferLabDialog}
                                                        className="text-darwin-primary-500 text-12 font-700 underline cursor-pointer"
                                                    >
                                                        How does it work?
                                                    </p>
                                                    <div className="flex items-center space-x-8 border border-darwin-accent-investigations-ochre-500 bg-darwin-accent-investigations-ochre-100 px-8 py-4 rounded-8">
                                                        <p className="text-darwin-neutral-1000 text-14 select-none">
                                                            Send to:
                                                        </p>
                                                        <div className="flex items-center space-x-4">
                                                            {selectedLab ? (
                                                                <img
                                                                    className="w-28 h-28 rounded-4"
                                                                    src={selectedLab?.pic}
                                                                />
                                                            ) : (
                                                                <div className="flex items-center justify-center w-28 h-28 rounded-4">
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        viewBox="0 0 512 512"
                                                                        className="w-16 h-16 fill-text-secondary"
                                                                    >
                                                                        <path d="M512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256zM92.93 126.9C64.8 162.3 48 207.2 48 256C48 370.9 141.1 464 256 464C304.8 464 349.7 447.2 385.1 419.1L92.93 126.9zM464 256C464 141.1 370.9 48 256 48C207.2 48 162.3 64.8 126.9 92.93L419.1 385.1C447.2 349.7 464 304.8 464 256z"></path>
                                                                    </svg>{' '}
                                                                </div>
                                                            )}

                                                            <select
                                                                className="text-14 font-700 text-darwin-neutral-1000 px-8 bg-transparent cursor-pointer"
                                                                value={
                                                                    selectedLabToReferId || 'none'
                                                                }
                                                                onChange={this.onSelectLabToRefer}
                                                            >
                                                                <option value="none">None</option>
                                                                {labsAvailableToRefer[0].network.map(
                                                                    (item) => (
                                                                        <option
                                                                            key={item._id}
                                                                            value={item._id}
                                                                        >
                                                                            {[
                                                                                item.name.f,
                                                                                item.name.l,
                                                                            ]
                                                                                .filter(Boolean)
                                                                                .join(' ')}
                                                                        </option>
                                                                    ),
                                                                )}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                </div>
                            </Fragment>
                        )}
                    </div>
                    <div className="mb-8"></div>
                    <MedTableRenderer
                        kind={kind}
                        elements={elements}
                        inventory={inventory}
                        elementsConfig={elementsConfig}
                        upsertElement={this.upsertElement}
                        onDeleteElement={this.deleteElement}
                        containerId={PAD_V2_CONTAINER}
                        isNameEditable={true}
                        onCopyDuration={this.onCopyDuration}
                        onCopyTestOn={this.onCopyTestOn}
                        onCopyRepeatOn={this.onCopyRepeatOn}
                        onSortEnd={this.onSortEnd}
                        REF_MAP={this.REF_MAP}
                        searchComponent={
                            <div className="mt-8 space-y-16">
                                {kind === MEDICINES_KIND && <DrugInteractionExceptionPanel />}
                                {kind === LAB_TESTS_KIND &&
                                    elements.find((t) => HIV_CONSENT_LAB_TESTS.has(t.id)) && (
                                        <ConsentFormSuggestion />
                                    )}
                                <div className="w-full">
                                    <PrescribeAutoSuggestV2
                                        kind={kind}
                                        onSelect={(r) => {
                                            this.onElementSuggetionSelected(r as MedicationsEntity);
                                            this.handleNextFocus(r as MedicationsEntity);
                                        }}
                                        hideCustomEntity={kind === LAB_TESTS_KIND ? true : false}
                                    />
                                </div>
                            </div>
                        }
                    />
                    <AddCustomLabTestModal
                        closeMe={this.addCustomLabTestModalToggleHandler}
                        visible={isAddCustomLabTestModalVisible}
                        onSelect={(r) => {
                            this.onElementSuggetionSelected(r as MedicationsEntity);
                            this.handleNextFocus(r as MedicationsEntity);
                            this.addCustomLabTestModalToggleHandler(r as MedicationsEntity);
                        }}
                    />
                </div>
            </Fragment>
        );
    }
}

export default connect((state: GlobalState, props: Props) => ({
    translate: getTranslate(state),
    labsAvailableToRefer:
        props.kind === LAB_TESTS_KIND ? getLabsAvailableToRefer(state) : undefined,
    selectedLabToReferId:
        props.kind === LAB_TESTS_KIND ? getSelectedLabToReferId(state) : undefined,
    elements: props.kind === LAB_TESTS_KIND ? getLabTests(state) : getMedications(state),
    inventory: getInventory(state),
    elementsConfig: getGenieSetting(state, 'medElements') as GeniePadElementsSettingItem[],
    pincode: getPincode(state),
}))(MedTableV2);
